import { RosterSearchSortInput } from '__generated__/graphql'
import {
    filterCodec,
    FILTER_KEYS,
    RosterFilterState,
} from './rosterFilterState'
import { toGraphQLRosterFilters } from './toGraphQLRosterFilters'
import { codec, useUrlState } from './useUrlState'

export type { RosterFilterState } from './rosterFilterState'
export { toGraphQLRosterFilters } from './toGraphQLRosterFilters'

const DEFAULT_LIMIT = 50

export type RosterSortField =
    | 'name'
    | 'createdAt'
    | 'createdBy'
    | 'company'
    | 'status'
    | 'type'
    | 'candidateCount'
    | 'processStatus'
    | 'processStatusUpdatedAt'
    | 'internalNote'
    | 'sourceStatus'
    | 'clientFavoritesCount'
    | 'consultants'
    | 'fundSize'
    | 'dateOnRoster'
    | 'sectors'
    | 'industries'
    | 'locations'
    | 'titles'

type SortOrder = 'asc' | 'desc'

const VALID_SORT_FIELDS: RosterSortField[] = [
    'name',
    'createdAt',
    'createdBy',
    'company',
    'status',
    'type',
    'candidateCount',
    'processStatus',
    'processStatusUpdatedAt',
    'internalNote',
    'sourceStatus',
    'clientFavoritesCount',
    'consultants',
    'fundSize',
    'dateOnRoster',
    'sectors',
    'industries',
    'locations',
    'titles',
]

export type RosterSortState = {
    field: RosterSortField
    order: SortOrder
}

function buildRosterSearchCodec(defaultStatuses: string[]) {
    return {
        page: codec.int({ defaultValue: 1 }),
        limit: codec.int(),
        sort: {
            default: [] as RosterSortState[],
            parse: (raw: string): RosterSortState[] =>
                raw.split(',').flatMap((part) => {
                    const sep = part.lastIndexOf(':')
                    if (sep === -1) return []
                    const field = part.slice(0, sep)
                    const order = part.slice(sep + 1)
                    if (
                        !VALID_SORT_FIELDS.includes(field as RosterSortField) ||
                        (order !== 'asc' && order !== 'desc')
                    )
                        return []
                    return [
                        {
                            field: field as RosterSortField,
                            order: order as SortOrder,
                        },
                    ]
                }),
            serialize: (val: RosterSortState[]): string | null =>
                val.length > 0
                    ? val.map((s) => `${s.field}:${s.order}`).join(',')
                    : null,
        },
        ...filterCodec,
        statuses: codec.stringArray({
            urlKey: 'status',
            defaultValue: defaultStatuses,
        }),
    }
}

const extractFilters = (state: Record<string, unknown>): RosterFilterState => {
    const filters = {} as Record<string, unknown>
    for (const key of FILTER_KEYS) {
        filters[key] = state[key]
    }
    return filters as unknown as RosterFilterState
}

export function useRosterSqlSearchUrlState(opts: {
    defaultStatuses: string[]
}) {
    const rosterSearchCodec = buildRosterSearchCodec(opts.defaultStatuses)
    const { state, setState } = useUrlState(rosterSearchCodec)

    const localDefaultFilters = Object.fromEntries(
        FILTER_KEYS.map((k) => {
            if (k === 'statuses') return [k, opts.defaultStatuses]
            return [k, filterCodec[k].default]
        })
    ) as RosterFilterState

    const page = state.page ?? 1
    const limit = state.limit ?? DEFAULT_LIMIT
    const sort = state.sort ?? ([] as RosterSortState[])
    const filters = extractFilters(state as unknown as Record<string, unknown>)

    const hasActiveFilters = FILTER_KEYS.some((key) => {
        const effectiveCodec =
            key === 'statuses' ? rosterSearchCodec.statuses : filterCodec[key]
        const serialize = effectiveCodec.serialize as (
            val: unknown
        ) => string | null
        return serialize((state as Record<string, unknown>)[key]) !== null
    })

    const graphQLSort: RosterSearchSortInput[] | undefined =
        sort.length > 0
            ? sort.map((s) => ({ field: s.field, order: s.order }))
            : undefined

    const setPage = (newPage: number) =>
        setState({ page: newPage } as Partial<typeof state>)

    const setLimit = (newLimit: number) =>
        setState({ limit: newLimit, page: 1 } as Partial<typeof state>)

    const setFilters = (newFilters: RosterFilterState) =>
        setState({ ...localDefaultFilters, ...newFilters, page: 1 } as Partial<
            typeof state
        >)

    const clearFilters = () =>
        setState({ ...localDefaultFilters, page: 1 } as Partial<typeof state>)

    const setSort = (newSort: RosterSortState[]) =>
        setState({ sort: newSort, page: 1 } as Partial<typeof state>)

    return {
        page,
        limit,
        filters,
        graphQLFilters: toGraphQLRosterFilters(filters),
        sort,
        graphQLSort,
        hasActiveFilters,
        setPage,
        setLimit,
        setFilters,
        clearFilters,
        setSort,
    }
}
